import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Brain, Clock, Search, Lightbulb, ChevronRight, Globe, Lock, FolderOpen, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import type { Quiz } from '../lib/types';

interface Question {
  id: string;
  quiz_id: string | null;
  ['Question 1']: string;
  ['Q1 Option 1']: string;
  ['Q1 Option 2']: string;
  ['Q1 Option 3']: string;
  ['Q1 Option 4']: string;
  ['Q1 correct_answer']: string;
  ['Q1 Explanation']: string;
  ['Question 2']: string;
  ['Q2 Option 1']: string;
  ['Q2 Option 2']: string;
  ['Q2 Option 3']: string;
  ['Q2 Option 4']: string;
  ['Q2 correct_answer']: string;
  ['Q2 Explanation']: string;
  ['Question 3']: string;
  ['Q3 Option 1']: string;
  ['Q3 Option 2']: string;
  ['Q3 Option 3']: string;
  ['Q3 Option 4']: string;
  ['Q3 correct_answer']: string;
  ['Q3 Explanation']: string;
  ['Question 4']: string;
  ['Q4 Option 1']: string;
  ['Q4 Option 2']: string;
  ['Q4 Option 3']: string;
  ['Q4 Option 4']: string;
  ['Q4 correct_answer']: string;
  ['Q4 Explanation']: string;
  ['Question 5']: string;
  ['Q5 Option 1']: string;
  ['Q5 Option 2']: string;
  ['Q5 Option 3']: string;
  ['Q5 Option 4']: string;
  ['Q5 correct_answer']: string;
  ['Q5 Explanation']: string;
  ['Question 6']: string;
  ['Q6 Option 1']: string;
  ['Q6 Option 2']: string;
  ['Q6 Option 3']: string;
  ['Q6 Option 4']: string;
  ['Q6 correct_answer']: string;
  ['Q6 Explanation']: string;
  ['Question 7']: string;
  ['Q7 Option 1']: string;
  ['Q7 Option 2']: string;
  ['Q7 Option 3']: string;
  ['Q7 Option 4']: string;
  ['Q7 correct_answer']: string;
  ['Q7 Explanation']: string;
  ['Question 8']: string;
  ['Q8 Option 1']: string;
  ['Q8 Option 2']: string;
  ['Q8 Option 3']: string;
  ['Q8 Option 4']: string;
  ['Q8 correct_answer']: string;
  ['Q8 Explanation']: string;
  ['Question 9']: string;
  ['Q9 Option 1']: string;
  ['Q9 Option 2']: string;
  ['Q9 Option 3']: string;
  ['Q9 Option 4']: string;
  ['Q9 correct_answer']: string;
  ['Q9 Explanation']: string;
  ['Question 10']: string;
  ['Q10 Option 1']: string;
  ['Q10 Option 2']: string;
  ['Q10 Option 3']: string;
  ['Q10 Option 4']: string;
  ['Q10 correct_answer']: string;
  ['Q10 Explanation']: string;
  created_at: string;
  updated_at: string;
}

interface RandomizedOption {
  text: string;
  originalIndex: number;
}

const QuizPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFlashcards, setShowFlashcards] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<(number | null)[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [flippedCards, setFlippedCards] = useState<boolean[]>([]);
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [currentAnswerCorrect, setCurrentAnswerCorrect] = useState(false);
  const [currentExplanation, setCurrentExplanation] = useState<string | null>(null);
  const [randomizedOptions, setRandomizedOptions] = useState<RandomizedOption[][]>([]);

  useEffect(() => {
    const fetchQuizData = async () => {
      try {
        if (!id) return;

        const { data: quizData, error: quizError } = await supabase
          .from('quiz')
          .select('*')
          .eq('id', id)
          .single();

        if (quizError) throw quizError;
        if (!quizData) throw new Error('Quiz non trouvé');

        setQuiz(quizData);

        const { data: questionsData, error: questionsError } = await supabase
          .from('questions')
          .select('*')
          .eq('quiz_id', id)
          .order('created_at', { ascending: true });

        if (questionsError) throw questionsError;
        
        if (questionsData && questionsData.length > 0) {
          setQuestions(questionsData);
          
          // Count total questions by checking which questions exist
          let count = 0;
          for (let i = 1; i <= 10; i++) {
            if (questionsData[0][`Question ${i}` as keyof Question]) {
              count++;
            } else {
              break;
            }
          }
          setTotalQuestions(count);
          
          // Initialize arrays based on question count
          setSelectedAnswers(new Array(count).fill(null));
          setFlippedCards(new Array(count).fill(false));
          
          // Initialize randomized options for all questions
          const allRandomizedOptions = Array(count).fill(null).map((_, index) => {
            const questionNum = index + 1;
            const options = [
              { text: questionsData[0][`Q${questionNum} Option 1` as keyof Question] as string, originalIndex: 0 },
              { text: questionsData[0][`Q${questionNum} Option 2` as keyof Question] as string, originalIndex: 1 },
              { text: questionsData[0][`Q${questionNum} Option 3` as keyof Question] as string, originalIndex: 2 },
              { text: questionsData[0][`Q${questionNum} Option 4` as keyof Question] as string, originalIndex: 3 }
            ].filter(option => option.text);

            // Only randomize if it's not a true/false question
            if (options.length !== 2 || 
                !options.some(opt => opt.text.toLowerCase() === 'vrai') ||
                !options.some(opt => opt.text.toLowerCase() === 'faux')) {
              return shuffleArray(options);
            }
            return options;
          });
          
          setRandomizedOptions(allRandomizedOptions);
        }
      } catch (err) {
        console.error('Error fetching quiz data:', err);
        setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuizData();
  }, [id]);

  const shuffleArray = <T extends unknown>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const handleAnswerSelect = async (randomizedIndex: number) => {
    if (showCorrectAnswer) return;

    const originalIndex = randomizedOptions[currentQuestionIndex][randomizedIndex].originalIndex;

    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestionIndex] = originalIndex;
    setSelectedAnswers(newAnswers);

    const currentQuestion = questions[0];
    const questionNumber = currentQuestionIndex + 1;
    const correctAnswerKey = `Q${questionNumber} correct_answer` as keyof Question;
    const explanationKey = `Q${questionNumber} Explanation` as keyof Question;
    const correctAnswer = currentQuestion[correctAnswerKey];
    const explanation = currentQuestion[explanationKey] as string;
    const selectedOption = currentQuestion[`Q${questionNumber} Option ${originalIndex + 1}` as keyof Question];

    const isCorrect = selectedOption === correctAnswer;
    setCurrentAnswerCorrect(isCorrect);
    setCurrentExplanation(explanation);

    await saveUserResponse(currentQuestion.id, selectedOption as string, isCorrect);

    setShowCorrectAnswer(true);
  };

  const saveUserResponse = async (questionId: string, selectedAnswer: string, isCorrect: boolean) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_responses')
        .insert({
          user_id: user.id,
          question_id: questionId,
          selected_answer: selectedAnswer,
          is_correct: isCorrect
        });

      if (error) throw error;
    } catch (err) {
      console.error('Error saving user response:', err);
    }
  };

  const handleNext = () => {
    setShowCorrectAnswer(false);
    setCurrentExplanation(null);
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setShowResults(true);
    }
  };

  const calculateScore = () => {
    let score = 0;
    const question = questions[0];

    selectedAnswers.forEach((answer, index) => {
      if (answer === null) return;
      const questionNumber = index + 1;
      const correctAnswerKey = `Q${questionNumber} correct_answer` as keyof Question;
      const correctAnswer = question[correctAnswerKey];
      const selectedOption = question[`Q${questionNumber} Option ${answer + 1}` as keyof Question];
      if (selectedOption === correctAnswer) score++;
    });

    return score;
  };

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers(new Array(totalQuestions).fill(null));
    setShowResults(false);
    setFlippedCards(new Array(totalQuestions).fill(false));
    setShowCorrectAnswer(false);
    setCurrentExplanation(null);
  };

  const toggleCard = (index: number) => {
    const newFlippedCards = [...flippedCards];
    newFlippedCards[index] = !newFlippedCards[index];
    setFlippedCards(newFlippedCards);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
      </div>
    );
  }

  if (error || !quiz || questions.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error || 'Quiz non trouvé'}</p>
          <Link
            to="/mes-fiches"
            className="mt-4 inline-flex items-center text-sm text-red-600 hover:text-red-500"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Retour à mes fiches
          </Link>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[0];
  const questionNumber = currentQuestionIndex + 1;
  const questionKey = `Question ${questionNumber}` as keyof Question;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow sm:rounded-lg overflow-hidden relative">
        {/* Result Overlay */}
        {showCorrectAnswer && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
              <div className="mb-4">
                {currentAnswerCorrect ? (
                  <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto animate-bounce" />
                ) : (
                  <XCircle className="h-16 w-16 text-red-500 mx-auto animate-pulse" />
                )}
              </div>
              <h2 className={`text-2xl font-bold mb-4 ${currentAnswerCorrect ? 'text-green-600' : 'text-red-600'}`}>
                {currentAnswerCorrect ? 'Bonne réponse !' : 'Mauvaise réponse'}
              </h2>
              {currentExplanation && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg text-left">
                  <h3 className="font-semibold text-gray-900 mb-2">Explication :</h3>
                  <p className="text-gray-700 text-sm">{currentExplanation}</p>
                </div>
              )}
              <button
                onClick={handleNext}
                className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
              >
                {currentQuestionIndex < totalQuestions - 1 ? (
                  <span className="inline-flex items-center justify-center">
                    Question suivante
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </span>
                ) : (
                  'Voir les résultats'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="px-4 py-3 sm:px-6 bg-gradient-to-r from-primary-600 to-secondary-600">
          <div className="flex justify-between items-start gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl font-bold text-white truncate">{quiz.title}</h1>
              <div className="flex items-center flex-wrap gap-x-4 mt-1">
                {quiz.fiche_id && (
                  <>
                    <Link
                      to={`/fiche/${quiz.fiche_id}`}
                      className="text-sm text-white/80 hover:text-white inline-flex items-center"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Retour à la fiche
                    </Link>
                    <Link
                      to={`/quiz/${id}/solution`}
                      className="text-sm text-white/80 hover:text-white inline-flex items-center"
                    >
                      <Search className="h-4 w-4 mr-1" />
                      <span className="hidden sm:inline">Voir la soluce</span>
                    </Link>
                  </>
                )}
              </div>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={() => setShowFlashcards(!showFlashcards)}
                className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium text-white bg-white/20 hover:bg-white/30 border border-white/30 transition-colors"
              >
                <Brain className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{showFlashcards ? 'Mode Quiz' : 'Mode Flashcards'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-5 sm:p-6">
          {showFlashcards ? (
            // Flashcards mode
            <div className="space-y-4">
              {Array.from({ length: totalQuestions }, (_, index) => {
                const num = index + 1;
                const questionKey = `Question ${num}` as keyof Question;
                const correctAnswerKey = `Q${num} correct_answer` as keyof Question;
                const explanationKey = `Q${num} Explanation` as keyof Question;
                const question = currentQuestion[questionKey];
                const answer = currentQuestion[correctAnswerKey];
                const explanation = currentQuestion[explanationKey];

                if (!question || !answer) return null;

                return (
                  <div
                    key={`flashcard-${num}`}
                    className={`cursor-pointer transition-all duration-500 ${
                      flippedCards[index] ? 'bg-primary-50' : 'bg-white'
                    } border rounded-lg p-6 shadow-sm hover:shadow-md`}
                    onClick={() => toggleCard(index)}
                  >
                    {flippedCards[index] ? (
                      <div>
                        <p className="text-gray-900 font-medium mb-2">{answer}</p>
                        {explanation && (
                          <p className="text-gray-600 text-sm mt-2 pt-2 border-t">
                            {explanation}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-900">{question}</p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            // Quiz mode
            <div>
              {!showResults ? (
                <div>
                  <div className="mb-8">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-lg font-medium text-gray-900">
                        Question {questionNumber} sur {totalQuestions}
                      </h2>
                      <span className="text-sm text-gray-500">
                        {Math.round((questionNumber / totalQuestions) * 100)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full"
                        style={{
                          width: `${(questionNumber / totalQuestions) * 100}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {currentQuestion[questionKey]}
                    </h3>
                    <div className="space-y-3">
                      {randomizedOptions[currentQuestionIndex].map((option, index) => (
                        <button
                          key={index}
                          className={`w-full text-left p-4 rounded-lg border transition-all ${
                            showCorrectAnswer
                              ? option.text === currentQuestion[`Q${questionNumber} correct_answer` as keyof Question]
                                ? 'border-green-500 bg-green-50'
                                : selectedAnswers[currentQuestionIndex] === option.originalIndex
                                ? 'border-red-500 bg-red-50'
                                : 'border-gray-300'
                              : selectedAnswers[currentQuestionIndex] === option.originalIndex
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-300 hover:border-primary-500'
                          }`}
                          onClick={() => handleAnswerSelect(index)}
                          disabled={showCorrectAnswer}
                        >
                          <div className="flex items-center justify-between">
                            <span>{option.text}</span>
                            {showCorrectAnswer &&
                              option.text === currentQuestion[`Q${questionNumber} correct_answer` as keyof Question] && (
                              <CheckCircle2 className="h-5 w-5 text-green-500" />
                            )}
                            {showCorrectAnswer &&
                              selectedAnswers[currentQuestionIndex] === option.originalIndex &&
                              option.text !== currentQuestion[`Q${questionNumber} correct_answer` as keyof Question] && (
                              <XCircle className="h-5 w-5 text-red-500" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                // Results
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Résultats</h2>
                  <p className="text-4xl font-bold text-primary-600 mb-4">
                    {calculateScore()} / {totalQuestions}
                  </p>
                  <p className="text-lg text-gray-600 mb-8">
                    {calculateScore() === totalQuestions
                      ? 'Parfait ! Vous avez tout bon !'
                      : calculateScore() >= Math.ceil(totalQuestions * 0.7)
                      ? 'Très bien ! Continuez comme ça !'
                      : 'Continuez à pratiquer pour vous améliorer !'}
                  </p>
                  <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-4">
                    <button
                      className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                      onClick={resetQuiz}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Recommencer
                    </button>
                    <Link
                      to={`/quiz/${id}/solution`}
                      className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-secondary-600 hover:text-secondary-700 hover:bg-gray-50"
                    >
                      <Search className="h-4 w-4 mr-2" />
                      Voir la soluce
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizPage;